import React, { useEffect, useState } from 'react';
import { getApiUrl } from '../utils/apiConfig';
import { db, auth } from '../services/firebase';
import { collection, addDoc, query, where, onSnapshot, doc, getDoc, serverTimestamp, orderBy, deleteDoc, updateDoc, setDoc } from 'firebase/firestore';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import {
  Typography, TextField, Button, Card, CardContent, List, Box, ListItem, ListItemText, IconButton, Grid, Avatar, Chip, CircularProgress, ToggleButton, ToggleButtonGroup,
  Dialog, DialogActions, DialogContent, DialogTitle, InputAdornment, Select, MenuItem, InputLabel, FormControl, ListItemAvatar
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import styles from '../styles/Dashboard.module.css';

const GerenciarFuncionarios = () => {
  const [funcionarios, setFuncionarios] = useState([]);
  const [novoFuncionario, setNovoFuncionario] = useState({ nome: '', email: '', cargo: '', telefone: '', papel: 'funcionario' });
  const [diasSemana, setDiasSemana] = useState([]);
  const [dataAniversario, setDataAniversario] = useState(null);
  const [foto, setFoto] = useState(null);
  const [documento, setDocumento] = useState(null);
  const [condominioId, setCondominioId] = useState('');
  const [userRole, setUserRole] = useState('');
  const [uploading, setUploading] = useState(false);
  const dias = ['Seg', 'Ter', 'Qua', 'Qui', 'Sex', 'Sáb', 'Dom'];

  // Estados para o modal de edição
  const [editDialog, setEditDialog] = useState(false);
  const [selectedFuncionario, setSelectedFuncionario] = useState(null);
  const [editFormData, setEditFormData] = useState({ nome: '', email: '', cargo: '', telefone: '', salario: '', horario_inicio: '', horario_fim: '', papel: 'funcionario' });
  const [editDiasSemana, setEditDiasSemana] = useState([]);
  const [editFoto, setEditFoto] = useState(null);
  const [visualizarFoto, setVisualizarFoto] = useState({ open: false, url: '', nome: '' });

  useEffect(() => {
    const unsubscribe = auth.onAuthStateChanged(async (user) => {
      if (user) {
        const userDocRef = doc(db, 'tb_usuarios', user.uid);
        const userDoc = await getDoc(userDocRef);
        if (userDoc.exists()) {
          const userData = userDoc.data();
          setCondominioId(userData.condominio_id);
          setUserRole(userData.papel);
        }
      }
    });
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    if (!condominioId) return;
    const q = query(collection(db, 'tb_funcionarios'), where('condominio_id', '==', condominioId), orderBy('nome', 'asc'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const list = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setFuncionarios(list);
    });
    return () => unsubscribe();
  }, [condominioId]);

  const handleFileUpload = async (file, path) => {
    if (!file) return { url: '', path: '' };
    const fileRef = ref(getStorage(), path);
    const metadata = {
      customMetadata: {
        'userRole': userRole,
        'condominioId': condominioId,
        'ownerId': auth.currentUser.uid
      }
    };

    await uploadBytes(fileRef, file, metadata);
    const url = await getDownloadURL(fileRef);
    return { url, path };
  };

  const handleCreateFuncionario = async () => {
    if (!novoFuncionario.nome || !novoFuncionario.cargo || !novoFuncionario.email) {
      alert('Nome, Email e Cargo são obrigatórios.');
      return;
    }
    setUploading(true);
    try {
      const docRef = doc(collection(db, 'tb_funcionarios'));
      const id = docRef.id;

      const fotoPath = foto ? `funcionarios/${condominioId}/${id}/foto_${foto.name}` : '';
      const docPath = documento ? `funcionarios/${condominioId}/${id}/doc_${documento.name}` : '';

      const { url: foto_url } = await handleFileUpload(foto, fotoPath);
      const { url: documento_url } = await handleFileUpload(documento, docPath);

      // Usar setDoc com a referência que já tem o ID gerado
      await setDoc(docRef, {
        ...novoFuncionario,
        salario: parseFloat(novoFuncionario.salario) || 0,
        horario_inicio: novoFuncionario.horario_inicio || '',
        horario_fim: novoFuncionario.horario_fim || '',
        dias_semana: diasSemana,
        aniversario: dataAniversario,
        foto_url,
        foto_path: fotoPath,
        documento_url,
        documento_path: docPath,
        condominio_id: condominioId,
        created_at: serverTimestamp(),
      });

      // Criar o usuário de autenticação para o funcionário
      const token = await auth.currentUser.getIdToken();
      const userResponse = await fetch(`${getApiUrl()}/api/create-funcionario-user`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify({
          email: novoFuncionario.email,
          nome: novoFuncionario.nome,
          condominio_id: condominioId,
          funcionario_id: id, // ID do documento do funcionário
          role: novoFuncionario.papel // Envia o papel selecionado
        }),
      });

      if (!userResponse.ok) {
        // Lê a mensagem de erro específica do backend
        const errorData = await userResponse.json();
        throw new Error(errorData.error || 'Falha ao criar o usuário de login.');
      }

      setNovoFuncionario({ nome: '', email: '', cargo: '', telefone: '', salario: '', horario_inicio: '', horario_fim: '', papel: 'funcionario' });
      setDiasSemana([]);
      setDataAniversario(null);
      setFoto(null);
      setDocumento(null);
      alert('Funcionário adicionado com sucesso!');
    } catch (error) {
      console.error("Erro ao adicionar funcionário:", error);
      alert('Erro ao adicionar funcionário: ' + error.message);
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteFuncionario = async (id) => {
    if (!window.confirm('Tem certeza que deseja remover este funcionário?')) return;
    try {
      const token = await auth.currentUser.getIdToken();
      const response = await fetch(`${getApiUrl()}/api/funcionarios/${id}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` },
      });
      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Erro ao remover funcionário.');
      }
      alert('Funcionário removido com sucesso.');
    } catch (error) {
      console.error("Erro ao remover funcionário:", error);
      alert('Erro ao remover funcionário: ' + error.message);
    }
  };

  const openEditDialog = async (funcionario) => {
    setSelectedFuncionario(funcionario);

    // Fetch user role if user_uid exists
    let currentRole = 'funcionario';
    if (funcionario.user_uid) {
      try {
        const userDocRef = doc(db, 'tb_usuarios', funcionario.user_uid);
        const userDoc = await getDoc(userDocRef);
        if (userDoc.exists()) {
          currentRole = userDoc.data().papel || 'funcionario';
        }
      } catch (error) {
        console.error("Erro ao buscar papel do usuário:", error);
      }
    }

    setEditFormData({
      nome: funcionario.nome,
      cargo: funcionario.cargo,
      email: funcionario.email || '',
      telefone: funcionario.telefone || '',
      salario: funcionario.salario || '',
      horario_inicio: funcionario.horario_inicio || '',
      horario_fim: funcionario.horario_fim || '',
      papel: currentRole
    });
    setEditDiasSemana(funcionario.dias_semana || []);
    setEditFoto(null); // Reset edit photo
    setEditDialog(true);
  };

  const handleUpdateFuncionario = async () => {
    if (!editFormData.nome || !editFormData.cargo || !editFormData.email) {
      alert('Nome e Cargo são obrigatórios.');
      return;
    }
    try {
      const funcionarioRef = doc(db, 'tb_funcionarios', selectedFuncionario.id);
      await updateDoc(funcionarioRef, {
        nome: editFormData.nome,
        cargo: editFormData.cargo,
        email: editFormData.email,
        telefone: editFormData.telefone,
        salario: parseFloat(editFormData.salario) || 0,
        horario_inicio: editFormData.horario_inicio,
        horario_fim: editFormData.horario_fim,
        dias_semana: editDiasSemana,
        dias_semana: editDiasSemana,
      });

      // Handle Photo Update
      if (editFoto) {
        let foto_url = selectedFuncionario.foto_url;
        let foto_path = selectedFuncionario.foto_path;

        // 1. Delete old photo if exists
        if (foto_path) {
          const token = await auth.currentUser.getIdToken();
          await fetch(`${getApiUrl()}/api/delete-old-morador-foto`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`,
            },
            body: JSON.stringify({ foto_path: foto_path }),
          }).catch(err => console.warn("Falha ao deletar foto antiga", err));
        }

        // 2. Upload new photo
        const newFotoPath = `funcionarios/${condominioId}/${selectedFuncionario.id}_${Date.now()}_${editFoto.name}`;
        const { url } = await handleFileUpload(editFoto, newFotoPath);

        // 3. Update doc with new photo
        await updateDoc(funcionarioRef, {
          foto_url: url,
          foto_path: newFotoPath
        });
      }

      // Update Role if changed and user_uid exists
      if (selectedFuncionario.user_uid && editFormData.papel) {
        const userDocRef = doc(db, 'tb_usuarios', selectedFuncionario.user_uid);
        // Check if papel is actually different or just force update it
        await updateDoc(userDocRef, {
          papel: editFormData.papel
        });
      }

      setEditDialog(false);
      setSelectedFuncionario(null);
      alert('Funcionário atualizado com sucesso!');
    } catch (error) {
      console.error("Erro ao atualizar funcionário:", error);
      alert('Erro ao atualizar funcionário: ' + error.message);
    }
  };


  const handleAbrirFoto = (func) => {
    if (func.foto_url) {
      setVisualizarFoto({ open: true, url: func.foto_url, nome: func.nome });
    }
  };

  const handleFecharFoto = () => {
    setVisualizarFoto({ open: false, url: '', nome: '' });
  };

  const isAdmin = userRole && ['sindico', 'sub_sindico', 'conselheiro'].includes(userRole);
  if (!isAdmin) return null; // Ou uma página de acesso negado

  return (
    <Box>
      <Box component="main" sx={{ flexGrow: 1, p: { xs: 1, sm: 3 }, background: 'linear-gradient(45deg, #283593 30%, #1976d2 90%)', minHeight: '100vh', overflowX: 'hidden' }}>
        <Typography variant="h4" gutterBottom sx={{ color: 'white' }}>Gerenciar Funcionários</Typography>

        <Card className={styles.card} sx={{ mb: 4 }}>
          <CardContent>
            <Typography variant="h5" className={styles.sectionTitle}>Adicionar Novo Funcionário</Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}><TextField fullWidth label="Nome Completo" value={novoFuncionario.nome} onChange={(e) => setNovoFuncionario({ ...novoFuncionario, nome: e.target.value })} /></Grid>
              <Grid item xs={12} md={4}><TextField fullWidth label="Email (para login)" type="email" value={novoFuncionario.email} onChange={(e) => setNovoFuncionario({ ...novoFuncionario, email: e.target.value })} /></Grid>
              <Grid item xs={12} md={4}><TextField fullWidth label="Cargo (Ex: Porteiro)" value={novoFuncionario.cargo} onChange={(e) => setNovoFuncionario({ ...novoFuncionario, cargo: e.target.value })} /></Grid>
              <Grid item xs={12} md={4}><TextField fullWidth label="Telefone" value={novoFuncionario.telefone} onChange={(e) => setNovoFuncionario({ ...novoFuncionario, telefone: e.target.value })} /></Grid>
              <Grid item xs={12} md={4}><DatePicker label="Data de Aniversário" value={dataAniversario} onChange={setDataAniversario} sx={{ width: '100%' }} /></Grid>
              <Grid item xs={12} md={4}><TextField fullWidth label="Salário" type="number" value={novoFuncionario.salario} onChange={(e) => setNovoFuncionario({ ...novoFuncionario, salario: e.target.value })} InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }} /></Grid>
              <Grid item xs={12} md={6}><TextField fullWidth label="Horário de Início" type="time" value={novoFuncionario.horario_inicio} onChange={(e) => setNovoFuncionario({ ...novoFuncionario, horario_inicio: e.target.value })} InputLabelProps={{ shrink: true }} /></Grid>
              <Grid item xs={12} md={6}><TextField fullWidth label="Horário de Fim" type="time" value={novoFuncionario.horario_fim} onChange={(e) => setNovoFuncionario({ ...novoFuncionario, horario_fim: e.target.value })} InputLabelProps={{ shrink: true }} /></Grid>
              <Grid item xs={12}>
                <Typography variant="subtitle2" gutterBottom sx={{ color: 'text.secondary' }}>Dias de Trabalho</Typography>
                <ToggleButtonGroup value={diasSemana} onChange={(e, newDias) => setDiasSemana(newDias)} aria-label="dias de trabalho" sx={{ flexWrap: 'wrap' }}>
                  {dias.map(dia => (
                    <ToggleButton key={dia} value={dia} aria-label={dia}>
                      {dia}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              </Grid>
              <Grid item xs={12} sm={6}>
                {!foto ? (
                  <Button variant="outlined" component="label" fullWidth>Anexar Foto
                    <input type="file" hidden accept="image/*" onChange={(e) => setFoto(e.target.files[0])} />
                  </Button>
                ) : (
                  <Chip label={foto.name} onDelete={() => setFoto(null)} />
                )}
              </Grid>
              <Grid item xs={12} sm={6}>
                {!documento ? (
                  <Button variant="outlined" component="label" fullWidth>Anexar Documento (PDF)
                    <input type="file" hidden accept="application/pdf" onChange={(e) => setDocumento(e.target.files[0])} />
                  </Button>
                ) : (
                  <Chip label={documento.name} onDelete={() => setDocumento(null)} />
                )}
              </Grid>
              <Grid item xs={12} md={4}>
                <FormControl fullWidth>
                  <InputLabel id="role-select-label">Nível de Acesso (Sistema)</InputLabel>
                  <Select
                    labelId="role-select-label"
                    value={novoFuncionario.papel}
                    label="Nível de Acesso (Sistema)"
                    onChange={(e) => setNovoFuncionario({ ...novoFuncionario, papel: e.target.value })}
                  >
                    <MenuItem value="funcionario">Funcionário (Padrão)</MenuItem>
                    <MenuItem value="zelador">Zelador (Líder)</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12}>
                <Button variant="contained" size="large" onClick={handleCreateFuncionario} disabled={uploading}>
                  Adicionar Funcionário
                </Button>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        <Card className={styles.card}>
          <CardContent>
            <Typography variant="h5" className={styles.sectionTitle}>Lista de Funcionários</Typography>
            <List>
              {funcionarios.map((func) => (
                <ListItem key={func.id} divider sx={{ flexDirection: { xs: 'column', sm: 'row' }, alignItems: { xs: 'flex-start', sm: 'center' } }}>
                  <ListItemAvatar>
                    <Avatar
                      src={func.foto_url}
                      sx={{ mr: 2, cursor: func.foto_url ? 'pointer' : 'default' }}
                      onClick={() => handleAbrirFoto(func)}
                    />
                  </ListItemAvatar>
                  <ListItemText
                    primary={`${func.nome} - ${func.cargo}`}
                    secondary={`Horário: ${func.horario_inicio || 'N/A'} às ${func.horario_fim || 'N/A'} (${func.dias_semana ? [...func.dias_semana].sort((a, b) => dias.indexOf(a) - dias.indexOf(b)).join(', ') : 'N/A'}) | Salário: R$ ${func.salario?.toFixed(2) || '0.00'}`}
                    sx={{ width: '100%', pr: { sm: 2 } }}
                  />
                  <Box sx={{ display: 'flex', gap: 1, alignSelf: { xs: 'flex-end', sm: 'center' }, mt: { xs: 1, sm: 0 } }}>
                    <IconButton aria-label="edit" onClick={() => openEditDialog(func)}>
                      <EditIcon />
                    </IconButton>
                    <IconButton aria-label="delete" onClick={() => handleDeleteFuncionario(func.id)}>
                      <DeleteIcon />
                    </IconButton>
                  </Box>
                </ListItem>
              ))}
            </List>
          </CardContent>
        </Card>
      </Box>

      {/* Modal de Edição */}
      <Dialog open={editDialog} onClose={() => setEditDialog(false)}>
        <DialogTitle>Editar Funcionário</DialogTitle>
        <DialogContent>
          <TextField autoFocus margin="dense" label="Nome Completo" type="text" fullWidth variant="standard" value={editFormData.nome} onChange={(e) => setEditFormData({ ...editFormData, nome: e.target.value })} />
          <TextField margin="dense" label="Email" type="email" fullWidth variant="standard" value={editFormData.email} onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })} />
          <TextField margin="dense" label="Cargo" type="text" fullWidth variant="standard" value={editFormData.cargo} onChange={(e) => setEditFormData({ ...editFormData, cargo: e.target.value })} />
          <TextField margin="dense" label="Telefone" type="text" fullWidth variant="standard" value={editFormData.telefone} onChange={(e) => setEditFormData({ ...editFormData, telefone: e.target.value })} />
          <TextField margin="dense" label="Salário" type="number" fullWidth variant="standard" value={editFormData.salario} onChange={(e) => setEditFormData({ ...editFormData, salario: e.target.value })} InputProps={{ startAdornment: <InputAdornment position="start">R$</InputAdornment> }} />
          <TextField margin="dense" label="Horário de Início" type="time" fullWidth variant="standard" value={editFormData.horario_inicio} onChange={(e) => setEditFormData({ ...editFormData, horario_inicio: e.target.value })} InputLabelProps={{ shrink: true }} />
          <TextField margin="dense" label="Horário de Fim" type="time" fullWidth variant="standard" value={editFormData.horario_fim} onChange={(e) => setEditFormData({ ...editFormData, horario_fim: e.target.value })} InputLabelProps={{ shrink: true }} />
          <FormControl fullWidth margin="dense">
            <InputLabel id="edit-role-select-label">Nível de Acesso (Sistema)</InputLabel>
            <Select
              labelId="edit-role-select-label"
              value={editFormData.papel}
              label="Nível de Acesso (Sistema)"
              onChange={(e) => setEditFormData({ ...editFormData, papel: e.target.value })}
            >
              <MenuItem value="funcionario">Funcionário (Padrão)</MenuItem>
              <MenuItem value="zelador">Zelador (Líder)</MenuItem>
            </Select>
          </FormControl>
          <Box sx={{ mt: 2 }}>
            <Typography variant="subtitle2" gutterBottom sx={{ color: 'text.secondary' }}>Dias de Trabalho</Typography>
            <ToggleButtonGroup value={editDiasSemana} onChange={(e, newDias) => setEditDiasSemana(newDias)} aria-label="dias de trabalho" size="small">
              {dias.map(dia => (
                <ToggleButton key={dia} value={dia} aria-label={dia}>
                  {dia}
                </ToggleButton>
              ))}
            </ToggleButtonGroup>
          </Box>
          <Box sx={{ mt: 2 }}>
            {!editFoto ? (
              <Button variant="outlined" component="label" fullWidth>
                Alterar Foto
                <input type="file" hidden accept="image/*" onChange={(e) => setEditFoto(e.target.files[0])} />
              </Button>
            ) : (
              <Chip label={editFoto.name} onDelete={() => setEditFoto(null)} />
            )}
          </Box>
          <Typography variant="caption" color="text.secondary" sx={{ mt: 2 }}>Para alterar documentos, remova e cadastre o funcionário novamente.</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditDialog(false)}>Cancelar</Button>
          <Button onClick={handleUpdateFuncionario}>Salvar</Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Visualização de Foto */}
      <Dialog open={visualizarFoto.open} onClose={handleFecharFoto}>
        <DialogTitle>{visualizarFoto.nome}</DialogTitle>
        <DialogContent>
          <img src={visualizarFoto.url} alt={`Foto de ${visualizarFoto.nome}`} style={{ width: '100%', height: 'auto', borderRadius: '8px' }} />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleFecharFoto}>Fechar</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default GerenciarFuncionarios;
